import React from 'react';
import { Edit, User, FileText, Calendar } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';

export const EditBorradorDialog = ({
  open,
  onOpenChange,
  unidades,
  contratos,
  selectedBorrador,
  borradorForm,
  setBorradorForm,
  saving,
  handleSaveBorrador,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            Editar Borrador
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Unidad selector */}
          <div>
            <Label className="text-sm text-slate-600">Unidad *</Label>
            <Select
              value={borradorForm.unidad_id}
              onValueChange={(v) => setBorradorForm(prev => ({...prev, unidad_id: v}))}
            >
              <SelectTrigger className={cn(!borradorForm.unidad_id && "border-amber-400")}>
                <SelectValue placeholder="Seleccionar unidad" />
              </SelectTrigger>
              <SelectContent>
                {unidades
                  .filter(u => u.en_administracion)
                  .filter(u => !contratos.some(c => c.unidad_id === u.unidad_id && c.estado === 'vigente'))
                  .map(u => (
                    <SelectItem key={u.unidad_id} value={u.unidad_id}>
                      {u.nombre_unidad}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            {selectedBorrador?.unidad_detectada && !borradorForm.unidad_id && (
              <p className="text-xs text-amber-600 mt-1">
                IA detectó: "{selectedBorrador.unidad_detectada}"
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-600">Nombre Comercial</Label>
              <Input
                value={borradorForm.nombre_comercial}
                onChange={(e) => setBorradorForm(prev => ({...prev, nombre_comercial: e.target.value}))}
                placeholder="Nombre de fantasía"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Razón Social *</Label>
              <Input
                value={borradorForm.razon_social}
                onChange={(e) => setBorradorForm(prev => ({...prev, razon_social: e.target.value}))}
                placeholder="Razón social"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-600">Fecha Inicio</Label>
              <Input
                type="date"
                value={borradorForm.fecha_inicio}
                onChange={(e) => {
                  const newFechaInicio = e.target.value;
                  setBorradorForm(prev => ({
                    ...prev,
                    fecha_inicio: newFechaInicio,
                    fecha_fin: prev.fecha_fin && prev.fecha_fin < newFechaInicio ? '' : prev.fecha_fin
                  }));
                }}
              />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Fecha Fin</Label>
              <Input
                type="date"
                value={borradorForm.fecha_fin}
                onChange={(e) => setBorradorForm(prev => ({...prev, fecha_fin: e.target.value}))}
                min={borradorForm.fecha_inicio || undefined}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-600">
                Monto al {new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </Label>
              <Input
                type="number"
                value={borradorForm.monto_inicial}
                onChange={(e) => setBorradorForm(prev => ({...prev, monto_inicial: e.target.value}))}
                placeholder="0"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Monto Actual (calculado)</Label>
              <Input
                type="number"
                value={borradorForm.monto_actual || ''}
                onChange={(e) => setBorradorForm(prev => ({...prev, monto_actual: e.target.value}))}
                placeholder="Se calcula automáticamente"
                className="bg-green-50 border-green-300"
              />
              <p className="text-xs text-slate-500 mt-1">Editable si el cálculo no es correcto</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-600">Moneda</Label>
              <Select
                value={borradorForm.moneda}
                onValueChange={(v) => setBorradorForm(prev => ({...prev, moneda: v}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ARS">ARS</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-600">Índice Ajuste</Label>
              <Select
                value={borradorForm.indice_tipo}
                onValueChange={(v) => setBorradorForm(prev => ({...prev, indice_tipo: v}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ipc">IPC/INDEC</SelectItem>
                  <SelectItem value="porcentaje">Porcentaje Fijo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-slate-600">Frecuencia</Label>
              <Select
                value={borradorForm.indice_frecuencia}
                onValueChange={(v) => setBorradorForm(prev => ({...prev, indice_frecuencia: v}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensual">Mensual</SelectItem>
                  <SelectItem value="bimestral">Bimestral</SelectItem>
                  <SelectItem value="trimestral">Trimestral</SelectItem>
                  <SelectItem value="cuatrimestral">Cuatrimestral</SelectItem>
                  <SelectItem value="semestral">Semestral</SelectItem>
                  <SelectItem value="anual">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sección Datos de Contacto */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Datos de Contacto
            </h4>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <Label className="text-sm text-slate-600">Contacto Comercial</Label>
                <Input
                  value={borradorForm.contacto_comercial_nombre || ''}
                  onChange={(e) => setBorradorForm(prev => ({...prev, contacto_comercial_nombre: e.target.value}))}
                  placeholder="Nombre"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Email Comercial</Label>
                <Input
                  type="email"
                  value={borradorForm.contacto_comercial_email || ''}
                  onChange={(e) => setBorradorForm(prev => ({...prev, contacto_comercial_email: e.target.value}))}
                  placeholder="email@ejemplo.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-600">Contacto Admin.</Label>
                <Input
                  value={borradorForm.contacto_admin_nombre || ''}
                  onChange={(e) => setBorradorForm(prev => ({...prev, contacto_admin_nombre: e.target.value}))}
                  placeholder="Nombre"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Email Admin.</Label>
                <Input
                  type="email"
                  value={borradorForm.contacto_admin_email || ''}
                  onChange={(e) => setBorradorForm(prev => ({...prev, contacto_admin_email: e.target.value}))}
                  placeholder="email@ejemplo.com"
                />
              </div>
            </div>
          </div>

          {/* Sección Datos Fiscales */}
          <div className="border-t border-slate-200 pt-4 mt-2">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Datos Fiscales
            </h4>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <Label className="text-sm text-slate-600">CUIT</Label>
                <Input
                  value={borradorForm.cuit || ''}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length > 11) value = value.slice(0, 11);

                    let formatted = '';
                    if (value.length > 0) formatted = value.slice(0, 2);
                    if (value.length > 2) formatted += '-' + value.slice(2, 10);
                    if (value.length > 10) formatted += '-' + value.slice(10, 11);

                    setBorradorForm(prev => ({...prev, cuit: formatted}));
                  }}
                  placeholder="20-12345678-9"
                  maxLength={13}
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Condición IVA</Label>
                <Select
                  value={borradorForm.condicion_iva || ''}
                  onValueChange={(v) => setBorradorForm(prev => ({...prev, condicion_iva: v}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="responsable_inscripto">Resp. Insc.</SelectItem>
                    <SelectItem value="exento">Exento</SelectItem>
                    <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                    <SelectItem value="monotributista">Monotributista</SelectItem>
                    <SelectItem value="del_exterior">Del Exterior</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mb-3">
              <Label className="text-sm text-slate-600">Domicilio Fiscal</Label>
              <Input
                value={borradorForm.domicilio_fiscal || ''}
                onChange={(e) => setBorradorForm(prev => ({...prev, domicilio_fiscal: e.target.value}))}
                placeholder="Dirección completa"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Email Facturación</Label>
              <Input
                type="email"
                value={borradorForm.email_facturacion || ''}
                onChange={(e) => setBorradorForm(prev => ({...prev, email_facturacion: e.target.value}))}
                placeholder="facturacion@ejemplo.com"
              />
            </div>
          </div>

          {/* Sección Condiciones de Cobro */}
          <div className="border-t border-slate-200 pt-4 mt-2">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Condiciones de Cobro
            </h4>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <Label className="text-sm text-slate-600">Día Emisión Factura</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={borradorForm.dia_emision || ''}
                  onChange={(e) => setBorradorForm(prev => ({...prev, dia_emision: e.target.value}))}
                  placeholder="1-28"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Día Vencimiento</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={borradorForm.dia_vencimiento || ''}
                  onChange={(e) => setBorradorForm(prev => ({...prev, dia_vencimiento: e.target.value}))}
                  placeholder="1-28"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="borrador_penalidad"
                checked={borradorForm.penalidad_enabled || false}
                onChange={(e) => setBorradorForm(prev => ({...prev, penalidad_enabled: e.target.checked}))}
                className="rounded border-slate-300"
              />
              <Label htmlFor="borrador_penalidad" className="text-sm cursor-pointer">
                Aplicar penalidades
              </Label>
            </div>
            {borradorForm.penalidad_enabled && (
              <div className="bg-slate-50 p-3 rounded-lg grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Días gracia</Label>
                  <Input
                    type="number"
                    value={borradorForm.penalidad_dias_gracia || 10}
                    onChange={(e) => setBorradorForm(prev => ({...prev, penalidad_dias_gracia: e.target.value}))}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">% 1er período</Label>
                  <Input
                    type="number"
                    value={borradorForm.penalidad_porcentaje_1 || 10}
                    onChange={(e) => setBorradorForm(prev => ({...prev, penalidad_porcentaje_1: e.target.value}))}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">% después</Label>
                  <Input
                    type="number"
                    value={borradorForm.penalidad_porcentaje_2 || 20}
                    onChange={(e) => setBorradorForm(prev => ({...prev, penalidad_porcentaje_2: e.target.value}))}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSaveBorrador} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
